<script setup lang="ts">
import 'vue-tel-input/vue-tel-input.css'
import { computed, onMounted, ref } from 'vue'
import { VueTelInput } from 'vue-tel-input'
import { Button } from '@/foundation/button'
import { Checkbox } from '@/foundation/checkbox'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/foundation/dialog'
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from '@/foundation/form'
import { updateCompanies } from '@/service/companies'
import PhoneInput from '@/views/companies/components/PhoneInput.vue'
import AutoFormLabel from './AutoFormLabel.vue'
import type { FieldProps } from './interface'
import { beautifyObjectName } from './utils'

const props = defineProps<FieldProps & { showCheckbox: boolean }>()
const capturedComponentField = ref()

const getComponentProps = (componentField: any) => {
  return {
    fieldName: componentField.name,
    modelValue: componentField.modelValue,
    ...props.config?.inputProps,
  }
}

onMounted(() => {
  if (props.config?.inputProps?.contact?.companyPhone) {
    toUseCompanyPhone.value = true
  } else {
    toUseCompanyPhone.value = false
  }
  if (props.config?.inputProps?.modelValue) {
    companyPhoneAsString.value = props.config?.inputProps.modelValue
  }
})

const updatedModelValue = (value: any) => {
  capturedComponentField.value['onUpdate:modelValue'](value)
}
const toUseCompanyPhone = ref(false)
const companyPhoneAsObject = ref()
const isPhoneModalOpen = ref(false)
const currentCompany = computed(() => props.config?.inputProps?.company)
const isCompanyPhoneValid = ref(true)
const companyPhoneAsString = ref()

const updateUseCompanyPhone = (value: boolean) => {
  toUseCompanyPhone.value = value
  props.config?.inputProps?.listeners?.onUpdateUseCompanyPhone(value)

  if (!value) {
    companyPhoneAsString.value = ''
    companyPhoneAsObject.value = undefined

    return
  }
  if (currentCompany.value?.phone) {
    companyPhoneAsString.value = currentCompany.value.phone
    capturedComponentField.value['onUpdate:modelValue'](currentCompany.value.phone)
  } else {
    isPhoneModalOpen.value = true
  }
}

const closePhoneModal = () => {
  isPhoneModalOpen.value = false
  toUseCompanyPhone.value = false
}
const changedPhoneNumber = (phoneValue: string, phoneObject: any) => {
  companyPhoneAsObject.value = phoneObject
  if (phoneObject && phoneObject.valid) {
    isCompanyPhoneValid.value = true
  } else if (phoneValue && !phoneObject.valid) {
    isCompanyPhoneValid.value = false
  }
}
const saveCompanyPhone = async () => {
  return new Promise((resolve, reject) => {
    const { onSuccess, onError } = updateCompanies(currentCompany.value.id, {
      ...currentCompany.value,
      phone: companyPhoneAsObject.value.number,
    })

    onSuccess(() => {
      isPhoneModalOpen.value = false
      capturedComponentField.value['onUpdate:modelValue'](companyPhoneAsString.value)
      props.config?.inputProps?.listeners?.onCompanyPhoneSave(companyPhoneAsString.value)
      resolve(true)
    })
    onError((error: any) => {
      console.error('Error saving company phone:', error)
      reject(error)
    })
  })
}

defineExpose({
  toUseCompanyPhone,
})
</script>

<template>
  <FormField v-slot="slotProps" :name="fieldName">
    <FormItem v-bind="$attrs" :ref="() => (capturedComponentField = slotProps.componentField)">
      <AutoFormLabel v-if="!config?.hideLabel" :required="required">
        {{ config?.label || beautifyObjectName(label ?? fieldName) }}
      </AutoFormLabel>
      <FormControl>
        <slot v-bind="slotProps">
          <div>
            <div v-if="props.config?.inputProps?.showCheckbox" class="flex items-center gap-1.5 my-3">
              <Checkbox id="use-company-phone" :is-checked="toUseCompanyPhone" @update:checked="updateUseCompanyPhone" />
              <label for="use-company-phone" class="text-sm leading-none"> Use company phone number </label>
              <Dialog :open="isPhoneModalOpen" @open-change="(open: boolean) => (isPhoneModalOpen = open)">
                <DialogContent class="!max-w-md">
                  <DialogHeader>
                    <DialogTitle>Phone number missing</DialogTitle>
                    <DialogDescription
                      >Add a company phone number for <strong>{{ currentCompany.name }}</strong></DialogDescription
                    >
                  </DialogHeader>
                  <div class="py-0">
                    <VueTelInput
                      v-model="companyPhoneAsString"
                      :value="companyPhoneAsObject"
                      class="mt-2 h-10 !rounded-md !border-input [&>_input]:!rounded-md text-sm focus-within:!shadow-none"
                      mode="international"
                      :input-options="{ placeholder: '+1 800 555 0100' }"
                      :auto-default-country="false"
                      default-country="US"
                      :default-value="''"
                      @on-input="changedPhoneNumber"
                    />
                    <p v-if="!isCompanyPhoneValid" role="alert" class="text-sm font-medium text-destructive">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-triangle-alert-icon h-3 w-3 inline-block mr-1 mb-1"
                      >
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path>
                        <path d="M12 9v4"></path>
                        <path d="M12 17h.01"></path>
                      </svg>
                      <span>Please enter a valid phone number.</span>
                    </p>
                  </div>
                  <DialogFooter>
                    <DialogClose>
                      <Button type="button" variant="secondary" @click="closePhoneModal"> Close </Button>
                    </DialogClose>
                    <Button
                      type="button"
                      :disabled="!isCompanyPhoneValid || !companyPhoneAsObject?.number"
                      @click="saveCompanyPhone"
                    >
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <PhoneInput
              class="!mt-0"
              :vmodel-as-string="companyPhoneAsString"
              v-bind="getComponentProps(slotProps.componentField)"
              :disabled="disabled || toUseCompanyPhone"
              @update:model-value="updatedModelValue"
            />
          </div>
        </slot>
      </FormControl>
      <FormDescription v-if="config?.description">
        {{ config.description }}
      </FormDescription>
      <div class="flex gap-2">
        <FormDescription
          v-if="slotProps.componentField.modelValue && config?.showCharacterCount"
          :class="{ 'text-red-500 font-semibold': slotProps.componentField.modelValue.length > config?.maxLength }"
        >
          {{ slotProps.componentField.modelValue.length }}/{{ config?.maxLength }}
        </FormDescription>
        <FormMessage />
      </div>
    </FormItem>
  </FormField>
</template>
